import {
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';

import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatOption, MatRipple } from '@angular/material/core';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';

import { Subject, takeUntil } from 'rxjs';

import { ESvgIcons } from 'src/app/core/enums/svg-icons';
import { MOBILE_SUGGESTIONS } from '../../constants/suggestions.const';

import { HeaderStateService } from 'src/app/core/services/header-state.service';

import { TravellersTransformerPipe } from '../../components/trip-wizard/travellers-transformer.pipe';

import { SwitcherComponent } from '../../components/switcher/switcher.component';
import { TripWizardComponent } from '../../components/trip-wizard/trip-wizard.component';
import { TravelersSettingComponent } from '../../components/travelers-setting/travelers-setting.component';
import { DateRangeSettingComponent } from '../../components/date-range-setting/date-range-setting.component';

import moment from 'moment';

@Component({
  selector: 'app-planning-form-sheet',
  imports: [
    MatFormField,
    MatSuffix,
    MatIcon,
    MatInput,
    MatRipple,
    AsyncPipe,
    MatOption,
    MatAutocomplete,
    MatAutocompleteTrigger,
    SwitcherComponent,
    TravelersSettingComponent,
    DateRangeSettingComponent,
  ],
  providers: [TravellersTransformerPipe],
  templateUrl: './planning-form-sheet.component.html',
  styleUrl: './planning-form-sheet.component.scss',
})
export class PlanningFormSheetComponent
  extends TripWizardComponent
  implements OnInit, OnDestroy
{
  @ViewChild('endInput') override readonly endInputRef: ElementRef;
  @ViewChild('startInInput') startInInput: ElementRef<HTMLInputElement>;
  @ViewChild('planningBoxContainer', { static: false })
  planningBoxContainer: ElementRef;

  protected readonly eSvgIcons = ESvgIcons;
  protected readonly suggestions = MOBILE_SUGGESTIONS;
  protected readonly config = [
    {
      label: 'Plan a trip',
      parameter: true,
    },
    {
      label: 'Explore city',
      parameter: false,
    },
  ];
  protected selectedIndex = 0;
  protected dateRangeData: any | null = null;
  protected planningFormView = {
    dateRangePreview: false,
    dateRangeEmpty: true,
    dateRangeText: '',
  };

  private roomsPayload: any;
  private dateRangePayload: any;
  private readonly destroy$: Subject<void> = new Subject<void>();

  readonly #headerStateService = inject(HeaderStateService);
  readonly #bottomSheetRef =
    inject<MatBottomSheetRef<PlanningFormSheetComponent>>(MatBottomSheetRef);

  ngOnInit() {
    super.ngOnInit();
    this.setUpTripStateView();
    this.headerBackBtnClickListener();
    this.setupStartInOptions();
    this.defaultView = this.trip().isPlanningTripLocation;
    this.selectedIndex = this.defaultView ? 0 : 1;

    if (this.trip().startingCountry) {
      this.setupEndInOptions();
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  travelersSettingsPayloadHandler(payload: any): void {
    this.roomsPayload = payload;
  }

  dateRangePayloadHandler(payload: any): void {
    this.dateRangePayload = payload;
  }

  submitPlanningForm(): void {
    this.trip.update((trip) =>
      trip.updateTrip({
        ...this.dateRangePayload,
        rooms: this.roomsPayload,
      }),
    );

    if (this.startInInput && this.startInInput.nativeElement) {
      this.navigateToTripPlanner(this.startInInput.nativeElement);

      if (this.defaultView && !this.trip().endingLocationId) return;
      this.#headerStateService.isBackBtnPresent = false;
      this.#bottomSheetRef.dismiss();
    }
  }

  setScrollBoxState(): void {
    if (this.planningBoxContainer && this.planningBoxContainer.nativeElement) {
      this.planningBoxContainer.nativeElement.classList.toggle(
        'planning-form-box__container--not-scrollable',
      );
    }
  }

  panelClosed(ref: MatAutocomplete, locationId: string, inputValue: any): void {
    const option = ref.options.first;
    setTimeout(() => {
      if (locationId || !option || inputValue?.length < 2) return;
      ref.options.first.select();
      ref.optionSelected.emit(new MatAutocompleteSelectedEvent(ref, option));
    }, 100);
  }

  private setUpTripStateView(): void {
    const { numDays, isSpecifyDates, tripStartDate, tripEndDate } = this.trip();
    this.dateRangeData = {
      numDays,
      isSpecifyDates,
      tripStartDate,
      tripEndDate,
    };
    const datePreview: boolean = !((tripStartDate && tripEndDate) || numDays);
    this.dateRangePayload = this.dateRangeData;
    this.planningFormView.dateRangeEmpty = datePreview;
    this.planningFormView.dateRangePreview = !datePreview;
    if (!isSpecifyDates) {
      this.planningFormView.dateRangeText = `${numDays} days`;
    } else {
      this.planningFormView.dateRangeText = `${moment(tripStartDate).format('MMM D, YYYY')} - ${moment(tripEndDate).format('MMM D, YYYY')}`;
    }
  }

  private headerBackBtnClickListener(): void {
    this.#headerStateService.isBackBtnPresent = true;
    this.#headerStateService.backBtnClickEmmit
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.#bottomSheetRef.dismiss();
        this.#headerStateService.isBackBtnPresent = false;
      });
  }
}
